import { useMany, useTranslate } from '@refinedev/core';
import type { HttpError } from '@refinedev/core';
import { Create, useForm, useSelect } from '@refinedev/antd';
import { Form, Select } from 'antd';
import type {
  ContractCreateIn,
  PublisherListOut,
} from '@/api/generated/schemas';
import { ContractFormFields } from '@/components/tenancy/ContractFormFields';
import {
  buildPublisherTree,
  toPublisherTreeData,
  PUBLISHER_TREE_LIMIT,
} from '@/components/user_catalog/publishers/publisherTree';
import {
  CONTRACT_STARTER_RATE,
  toContractPayload,
} from '@/components/tenancy/contractFormValues';
import type { ContractFormValues } from '@/components/tenancy/contractFormValues';
import { useOrganizationPublisherLinks } from '@/hooks/tenancy/useOrganizationPublisherLinks';
import type { ContractOut } from './constants';

type ContractCreateFormValues = ContractFormValues & {
  organization_id: number;
};

export const ContractCreate = () => {
  const t = useTranslate();
  const { formProps, saveButtonProps } = useForm<
    ContractOut,
    HttpError,
    ContractCreateIn
  >({
    resource: 'tenancy.contracts',
  });

  const { selectProps: organizationSelectProps } = useSelect({
    resource: 'tenancy.organizations',
    optionLabel: 'name',
    optionValue: 'id',
    onSearch: (value) => [{ field: 'q', operator: 'contains', value }],
  });

  const organizationId = Form.useWatch<number | undefined>(
    'organization_id',
    formProps.form
  );

  // Contracts may only target the selected organization's own publishers —
  // the backend checks membership on the contract's start.
  const { publisherIds } = useOrganizationPublisherLinks(organizationId);
  const { result: publishersResult } = useMany<PublisherListOut>({
    resource: 'user_catalog.publishers',
    ids: publisherIds,
    // The tree offers only what comes back, and `getMany` would otherwise
    // fall back to the server's default of 100.
    meta: { params: { limit: PUBLISHER_TREE_LIMIT } },
    queryOptions: { enabled: publisherIds.length > 0 },
  });
  const publisherTreeData = toPublisherTreeData(
    buildPublisherTree(publishersResult?.data ?? [])
  );

  return (
    <Create saveButtonProps={saveButtonProps}>
      <Form
        {...formProps}
        layout="vertical"
        initialValues={{ rates: [{ ...CONTRACT_STARTER_RATE }] }}
        onFinish={(values) => {
          // antd types onFinish after the mutation payload, but the raw
          // form values (Dayjs valid_from) arrive here.
          const { organization_id, ...rest } =
            values as unknown as ContractCreateFormValues;
          return formProps.onFinish?.({
            organization_id,
            ...toContractPayload(rest),
          });
        }}
      >
        <Form.Item
          name="organization_id"
          label={t('tenancy.contracts.fields.organization')}
          rules={[{ required: true }]}
        >
          <Select
            {...organizationSelectProps}
            showSearch
            filterOption={false}
            onChange={() =>
              // A publisher from the previous organization is meaningless.
              formProps.form?.setFieldValue('publisher_id', undefined)
            }
          />
        </Form.Item>

        <ContractFormFields
          publisherTreeData={publisherTreeData}
          publisherDisabled={organizationId == null}
        />
      </Form>
    </Create>
  );
};
